import React, { useState, useEffect, useMemo, useRef, useCallback } from 'react';
import { C } from './constants';
import { getPeriodeInfo } from './App';
import { chargerAnnuaire, membresEffectifs, FlechesOrdre, deplacerDansListe, estMelissaUid } from './EquipeGains';
import { SEUIL_DIRECTEUR, PV_MIN } from './PlanMarketing';
import { BoutonRemplirMihi } from './ChiffresMihi';

// ─────────────────────────────────────────────────────────────────────────────
// 🌟 DIRECTEURS À VENIR (Objectifs → Calcul du reste)
// Les personnes de l'équipe qui vont bientôt atteindre 5 000 € : une jauge par personne,
// et un bouton pour calculer le reste qualifiant AVEC ou SANS elles
// (quand une personne devient directrice, tout son groupe sort de mon volume).
// Données dans db-obj-perso → obj.dirAVenir = { periode, mode, membres:[{id,uid,nom,caManuel,inclus}] }
// ─────────────────────────────────────────────────────────────────────────────

export const NB_MAX_AVENIR = 6;

const nombre = (v) => {
  const n = parseFloat(String(v == null ? "" : v).replace(/\s/g, "").replace(",", "."));
  return isNaN(n) || n < 0 ? 0 : n;
};
const arrondi = (n) => Math.round(n * 100) / 100;
const fmtE = (n) => {
  const v = arrondi(n);
  return v.toLocaleString("fr-FR", Number.isInteger(v) ? { maximumFractionDigits: 0 } : { minimumFractionDigits: 2, maximumFractionDigits: 2 }) + " €";
};
const dateCourte = (ms) => {
  if (!ms) return "";
  const d = new Date(ms);
  return isNaN(d.getTime()) ? "" : d.toLocaleDateString("fr-FR", { day: "numeric", month: "short" });
};
const joli = (a, cle) => {
  const brut = `${(a && a.prenom) || ""} ${(a && a.nom) || ""}`.trim();
  if (brut) return brut;
  return String(cle || "").split("-").map((w) => w.charAt(0).toUpperCase() + w.slice(1)).join(" ");
};
const periodeCle = () => { try { return String(getPeriodeInfo().periodNum); } catch { return ""; } };
const idUnique = () => Date.now() * 1000 + Math.floor(Math.random() * 1000);

// Données propres : on garde les personnes, mais les chiffres tapés à la main repartent à zéro à chaque nouvelle période
function normaliser(brut) {
  const b = brut && typeof brut === "object" ? brut : {};
  const periode = periodeCle();
  const nouvelle = !!b.periode && b.periode !== periode;
  return {
    periode,
    mode: b.mode === "avec" ? "avec" : "sans",
    membres: (Array.isArray(b.membres) ? b.membres : []).slice(0, NB_MAX_AVENIR).map((m) => ({
      id: m.id || idUnique(),
      uid: m.uid || null,
      nom: m.nom || "",
      caManuel: nouvelle ? "" : (m.caManuel == null ? "" : String(m.caManuel)),
      dernierCa: nouvelle ? (String(m.caManuel == null ? "" : m.caManuel).trim() ? String(m.caManuel) : (m.dernierCa || "")) : (m.dernierCa || ""),
      inclus: m.inclus !== false,
      pvManuel: nouvelle ? "" : (m.pvManuel == null ? "" : String(m.pvManuel)),
    })),
  };
}

// Tout ce dont l'écran a besoin (état, calculs, actions)
export function useDirAVenir(obj, save, enPlace = [], uid = null) {
  const objRef = useRef(obj);
  objRef.current = obj;
  const [local, setLocal] = useState(() => normaliser(obj && obj.dirAVenir));
  const [inited, setInited] = useState(!!(obj && obj.dirAVenir));
  const [annuaire, setAnnuaire] = useState(undefined);   // undefined = chargement, null = indisponible

  useEffect(() => {
    let vivant = true;
    chargerAnnuaire(uid).then((a) => { if (vivant) setAnnuaire(a); });
    return () => { vivant = false; };
  }, [uid]);

  // L'objet des objectifs peut arriver après le premier affichage : on prend alors ses données une seule fois
  useEffect(() => {
    if (!inited && obj && obj.dirAVenir) { setLocal(normaliser(obj.dirAVenir)); setInited(true); }
  }, [obj, inited]);

  const maj = useCallback((next) => {
    setLocal(next);
    try { save({ ...objRef.current, dirAVenir: next }); } catch { /* ignore */ }
  }, [save]);

  const liste = useMemo(() => {
    // Les directeurs déjà en place sont ajoutés au calcul pour repérer une personne déjà comprise dans leur groupe
    const dejaDir = (enPlace || []).filter((d) => d && d.uid).map((d) => ({ id: "dir-" + d.uid, uid: d.uid, nom: d.nom || "ton directeur", pgvManuel: "", pvManuel: "" }));
    const equipe = { membres: [...dejaDir, ...local.membres.map((m) => ({ id: m.id, uid: m.uid, nom: m.nom, pgvManuel: m.caManuel, pvManuel: m.pvManuel || "" }))] };
    const eff = membresEffectifs(equipe, annuaire || null).slice(dejaDir.length);
    return local.membres.map((m, i) => {
      const e = eff[i] || {};
      return { ...m, pgv: e.pgv || 0, dernierCa: m.dernierCa || "", source: e.source, nbSous: e.nbSous || 0, pvOk: e.pvOk, maj: e.maj, exclu: !!e.exclu, inclusDans: e.inclusDans || null };
    });
  }, [local, annuaire, enPlace]);

  const totalInclus = arrondi(liste.filter((m) => m.inclus && !m.exclu).reduce((s, m) => s + m.pgv, 0));

  return {
    liste, annuaire, mode: local.mode, totalInclus,
    peutBasculer: liste.some((m) => m.inclus && !m.exclu && m.pgv > 0),
    ajouter: (uid, nom) => {
      if (local.membres.length >= NB_MAX_AVENIR) return;
      maj({ ...local, membres: [...local.membres, { id: idUnique(), uid, nom, caManuel: "", inclus: true }] });
    },
    ajouterManuel: (nom, ca) => {
      if (local.membres.length >= NB_MAX_AVENIR || !String(nom || "").trim()) return;
      maj({ ...local, membres: [...local.membres, { id: idUnique(), uid: null, nom: String(nom).trim(), caManuel: ca || "", inclus: true }] });
    },
    retirer: (id) => maj({ ...local, membres: local.membres.filter((m) => m.id !== id) }),
    setCa: (id, v) => maj({ ...local, membres: local.membres.map((m) => (m.id === id ? { ...m, caManuel: v } : m)) }),
    // Chiffres venus de Mihi : { id: { ca } } (volume de groupe seulement) pour plusieurs personnes d'un coup
    setChiffres: (parId) => maj({ ...local, membres: local.membres.map((m) => (parId[m.id] ? { ...m, caManuel: parId[m.id].ca } : m)) }),
    setInclus: (id, v) => maj({ ...local, membres: local.membres.map((m) => (m.id === id ? { ...m, inclus: v } : m)) }),
    setMode: (mode) => maj({ ...local, mode }),
    deplacer: (id, delta) => maj({ ...local, membres: deplacerDansListe(local.membres, id, delta) }),
  };
}

// ── Le bouton « avec / sans mes futurs directeurs » (affiché dans le résultat du calcul) ──
export function BasculeFuturs({ av, resteSans, resteAvec }) {
  if (!av.peutBasculer) return null;
  const btn = (actif) => ({ flex: 1, padding: ".5rem .4rem", fontSize: ".68rem", fontWeight: 700, borderRadius: 9, cursor: "pointer", fontFamily: "inherit", lineHeight: 1.3, border: `1.5px solid ${actif ? C.or : "rgba(255,255,255,.25)"}`, background: actif ? C.or : "transparent", color: actif ? C.brun : C.pale });
  return (
    <div style={{ marginTop: ".6rem" }}>
      <div style={{ fontSize: ".58rem", fontWeight: 700, color: C.or, letterSpacing: ".1em", textTransform: "uppercase", marginBottom: ".3rem" }}>🌟 Compter mes futurs directeurs ?</div>
      <div style={{ display: "flex", gap: ".4rem" }}>
        <button onClick={() => av.setMode("sans")} style={btn(av.mode === "sans")}>Sans eux<br /><span style={{ fontWeight: 600, opacity: .85 }}>{fmtE(resteSans)}</span></button>
        <button onClick={() => av.setMode("avec")} style={btn(av.mode === "avec")}>Avec eux<br /><span style={{ fontWeight: 600, opacity: .85 }}>{fmtE(resteAvec)}</span></button>
      </div>
      <div style={{ fontSize: ".58rem", color: C.pale, opacity: .85, marginTop: ".3rem", lineHeight: 1.45 }}>
        « Avec eux » : le groupe de chaque futur directeur ({fmtE(av.totalInclus)}) sort de ton reste, comme si elles étaient déjà directrices.
      </div>
    </div>
  );
}

// ── L'onglet « Directeurs à venir » ──
export function DirecteursAVenirOnglet({ av, uid, personnes = null, dejaDirecteurs = [] }) {
  const [recherche, setRecherche] = useState("");
  const [manuelNom, setManuelNom] = useState("");
  const [manuelCa, setManuelCa] = useState("");
  const complet = av.liste.length >= NB_MAX_AVENIR;

  const dejaLa = new Set([...av.liste.map((m) => m.uid), ...dejaDirecteurs].filter(Boolean));
  const q = recherche.trim().toLowerCase();
  // Même liste de personnes que pour les directeurs en place (accès + annuaire) ; sinon l'annuaire seul
  const visibles = estMelissaUid(uid) ? null : new Set(av.annuaire ? Object.entries(av.annuaire).map(([k, a]) => (a && a.uid) || k) : []);
  const source = Array.isArray(personnes) && personnes.length
    ? personnes.filter((p) => !visibles || visibles.has(p.id)).map((p) => ({ uid: p.id, nom: `${p.prenom || ""} ${p.nom || ""}`.trim() || joli(null, p.id) }))
    : av.annuaire ? Object.entries(av.annuaire).map(([k, a]) => ({ uid: (a && a.uid) || k, nom: joli(a, k) })) : [];
  const resultats = q.length >= 2
    ? source.filter((r) => r.uid !== uid && !dejaLa.has(r.uid) && r.nom.toLowerCase().includes(q)).slice(0, 8)
    : [];

  const champ = { width: "100%", boxSizing: "border-box", border: `1.5px solid ${C.pale}`, borderRadius: 9, padding: ".5rem .65rem", fontSize: ".82rem", fontFamily: "inherit", color: C.brun, background: C.creme, outline: "none" };

  return (
    <div id="decouverte-reste-avenir">
      <div style={{ fontSize: ".66rem", color: C.gris, lineHeight: 1.55, marginBottom: ".6rem" }}>
        Les personnes de ton équipe qui vont bientôt devenir directrices (<strong>5 000 €</strong> de volume avec 25 € de commande perso). Quand elles passent directrices, leur groupe sort de ton volume : le bouton « Avec / Sans eux », dans le <strong>Reste qualifiant</strong> juste en dessous, te montre les deux calculs.
      </div>

      {av.liste.length > 0 && (
        <BoutonRemplirMihi
          uid={uid}
          titre="Remplir les chiffres depuis Mihi"
          aide="Reprend uniquement le volume de groupe (VGP) de chaque personne de cette liste. Aucun volume perso, aucune vente."
          items={av.liste.map((m) => ({ key: m.id, nom: m.nom, uid: m.uid || null }))}
          decrire={(mb) => `VGP ${fmtE(mb.vgp)}`}
          onApply={(liste) => {
            const parId = {};
            liste.forEach(({ key, membre }) => { parId[key] = { ca: String(Math.round(membre.vgp * 100) / 100) }; });
            av.setChiffres(parId);
          }}
        />
      )}

      {av.liste.length === 0 && (
        <div style={{ background: C.creme, borderRadius: 10, padding: ".7rem .8rem", fontSize: ".72rem", color: C.gris, lineHeight: 1.5, marginBottom: ".6rem" }}>
          Personne pour l'instant. Cherche une personne de ton équipe ci-dessous : son chiffre remonte tout seul et tu vois sa jauge vers 5 000 €.
        </div>
      )}

      {av.liste.map((m, idx) => {
        const pct = Math.min(100, (m.pgv / SEUIL_DIRECTEUR) * 100);
        const manque = Math.max(0, SEUIL_DIRECTEUR - m.pgv);
        const atteint = m.pgv >= SEUIL_DIRECTEUR;
        const couleur = atteint ? C.vert : pct >= 60 ? C.or : C.rose;
        return (
          <div key={m.id} style={{ background: C.creme, border: `1px solid ${C.pale}`, borderRadius: 11, padding: ".65rem .75rem", marginBottom: ".5rem", opacity: m.exclu ? .75 : 1 }}>
            <div style={{ display: "flex", alignItems: "center", gap: ".5rem" }}>
              <FlechesOrdre premier={idx === 0} dernier={idx === av.liste.length - 1} onHaut={() => av.deplacer(m.id, -1)} onBas={() => av.deplacer(m.id, 1)} />
              <div style={{ flex: 1, minWidth: 0 }}>
                <div style={{ fontSize: ".84rem", fontWeight: 700, color: C.brun, wordBreak: "break-word" }}>🌟 {m.nom}</div>
              </div>
              <button onClick={() => av.retirer(m.id)} aria-label="Retirer" style={{ background: "none", border: "none", color: C.gris, fontSize: "1.1rem", cursor: "pointer", padding: 0, lineHeight: 1 }}>×</button>
            </div>

            {m.exclu ? (
              <div style={{ fontSize: ".62rem", color: C.vert, marginTop: ".3rem", lineHeight: 1.45 }}>✓ Déjà comptée dans le groupe de <strong>{m.inclusDans}</strong> : son volume n'est pas compté deux fois.</div>
            ) : (
              <>
                {/* JAUGE 5 000 */}
                <div style={{ display: "flex", justifyContent: "space-between", alignItems: "baseline", marginTop: ".45rem" }}>
                  <span style={{ fontSize: ".6rem", color: C.gris, fontWeight: 600 }}>Jauge Directeur</span>
                  <span style={{ fontSize: ".72rem", fontWeight: 700, color: couleur }}>{fmtE(m.pgv)} <span style={{ fontWeight: 400, color: C.gris }}>/ {fmtE(SEUIL_DIRECTEUR)}</span></span>
                </div>
                <div style={{ height: 9, background: C.pale, borderRadius: 10, overflow: "hidden", margin: ".2rem 0" }}>
                  <div style={{ height: "100%", width: Math.max(2, Math.round(pct)) + "%", background: couleur, borderRadius: 10, transition: "width .5s" }} />
                </div>
                <div style={{ fontSize: ".66rem", fontWeight: 700, color: couleur }}>
                  {atteint ? "🎉 5 000 € atteints : son groupe sort de ton volume" : `Il manque ${fmtE(manque)} · ${Math.round(pct)} %`}
                </div>

                {m.source === "auto" && <div style={{ fontSize: ".56rem", color: C.vert, marginTop: ".25rem", lineHeight: 1.45 }}>✓ Chiffre remonté automatiquement{m.maj ? ` · mis à jour le ${dateCourte(m.maj)}` : ""}{m.nbSous > 0 ? ` · son équipe comprise (${m.nbSous} pers.)` : ""}</div>}
                {m.source === "arbre-seul" && <div style={{ fontSize: ".58rem", color: "#B26A00", marginTop: ".25rem", lineHeight: 1.45 }}>⚠️ Pas mise à jour cette période : on compte seulement ce que son équipe a remonté. Tape son vrai chiffre si tu le connais.</div>}
                {(m.source === "obsolete" || m.source === "vide") && <div style={{ fontSize: ".58rem", color: "#B26A00", marginTop: ".25rem", lineHeight: 1.45 }}>⚠️ Son chiffre n'est pas à jour : tape-le à la main.</div>}
                {m.pvOk === false && <div style={{ fontSize: ".58rem", color: "#B26A00", marginTop: ".2rem" }}>⚠️ Elle n'a pas encore {PV_MIN} € de commande perso : il lui faut ça aussi pour être directrice.</div>}

                {m.dernierCa && !m.caManuel && m.source !== "auto" && <div style={{ fontSize: ".58rem", color: C.gris, marginTop: ".2rem" }}>Période précédente : {fmtE(nombre(m.dernierCa))}</div>}
                <div style={{ display: "flex", alignItems: "center", gap: ".4rem", marginTop: ".45rem" }}>
                  <span style={{ fontSize: ".62rem", color: C.gris, flexShrink: 0 }}>Son CA :</span>
                  <input type="text" inputMode="decimal" value={m.caManuel} placeholder={m.source === "auto" || m.source === "arbre-seul" ? `Auto : ${fmtE(m.pgv)}` : "À taper"}
                    onChange={(e) => av.setCa(m.id, e.target.value)}
                    style={{ ...champ, flex: 1, fontSize: ".82rem", padding: ".35rem .5rem", textAlign: "right", background: "white" }} />
                  <span style={{ fontSize: ".65rem", color: C.gris }}>€</span>
                </div>
                <label style={{ display: "flex", alignItems: "center", gap: ".4rem", marginTop: ".4rem", fontSize: ".62rem", color: C.brun, cursor: "pointer" }}>
                  <input type="checkbox" checked={m.inclus} onChange={(e) => av.setInclus(m.id, e.target.checked)} />
                  Compter dans « Avec mes futurs directeurs »
                </label>
              </>
            )}
          </div>
        );
      })}

      {/* RECHERCHE */}
      {!complet && (
        <div style={{ marginTop: ".3rem" }}>
          {av.annuaire === undefined && source.length === 0 && <div style={{ fontSize: ".66rem", color: C.gris }}>Chargement de l'annuaire…</div>}
          {av.annuaire === null && source.length === 0 && <div style={{ fontSize: ".66rem", color: "#B26A00" }}>Annuaire indisponible pour l'instant : ajoute la personne à la main ci-dessous.</div>}
          {source.length > 0 && (
            <>
              <input type="text" value={recherche} onChange={(e) => setRecherche(e.target.value)} placeholder="🔍 Chercher une personne par son prénom ou nom" style={champ} />
              {resultats.map((r) => (
                <div key={r.uid} onClick={() => { av.ajouter(r.uid, r.nom); setRecherche(""); }}
                  style={{ display: "flex", justifyContent: "space-between", alignItems: "center", padding: ".45rem .3rem", borderBottom: `1px solid ${C.pale}`, cursor: "pointer" }}>
                  <span style={{ fontSize: ".78rem", color: C.brun, fontWeight: 600 }}>{r.nom}</span>
                  <span style={{ fontSize: ".6rem", color: C.vert }}>+ Ajouter</span>
                </div>
              ))}
              {q.length >= 2 && resultats.length === 0 && <div style={{ fontSize: ".64rem", color: C.gris, padding: ".4rem .2rem" }}>Personne trouvée. Ajoute-la à la main ci-dessous.</div>}
            </>
          )}
          <div style={{ marginTop: ".6rem", paddingTop: ".5rem", borderTop: `1px dashed ${C.pale}` }}>
            <div style={{ fontSize: ".62rem", color: C.gris, marginBottom: ".3rem" }}>Ou ajouter quelqu'un à la main :</div>
            <div style={{ display: "flex", gap: ".4rem" }}>
              <input type="text" value={manuelNom} onChange={(e) => setManuelNom(e.target.value)} placeholder="Prénom" style={{ ...champ, flex: 1 }} />
              <input type="text" inputMode="decimal" value={manuelCa} onChange={(e) => setManuelCa(e.target.value)} placeholder="CA €" style={{ ...champ, width: 84 }}
                onKeyDown={(e) => { if (e.key === "Enter" && manuelNom.trim()) { av.ajouterManuel(manuelNom, manuelCa); setManuelNom(""); setManuelCa(""); } }} />
              <button onClick={() => { av.ajouterManuel(manuelNom, manuelCa); setManuelNom(""); setManuelCa(""); }} disabled={!manuelNom.trim()}
                style={{ background: manuelNom.trim() ? C.rose : C.pale, color: "white", border: "none", borderRadius: 9, padding: "0 .8rem", fontSize: ".95rem", fontWeight: 700, cursor: "pointer", fontFamily: "inherit" }}>+</button>
            </div>
          </div>
        </div>
      )}
      {complet && <div style={{ fontSize: ".62rem", color: C.gris, textAlign: "center", marginTop: ".3rem" }}>Maximum {NB_MAX_AVENIR} personnes. Retire-en une pour en ajouter une autre.</div>}
    </div>
  );
}
